// packages/kb-view/src/kb-link-target.ts
//
// LAT-1867 — the one rule for WHERE a KB-internal link target points, in the
// one place every consumer reads it from.
//
// Given a link target as authored in a KB file body and the KB path of the
// file it was authored in, produce the canonical KB path it addresses — or the
// specific reason it addresses nothing. The rule is PRD §6 / ADR 0038's:
//
//   * a relative target resolves against the SOURCE FILE'S DIRECTORY, with
//     `.` and `..` segments folded and empty segments ignored;
//   * a leading `/` resolves against the KB root, whatever the source's depth;
//   * a target that climbs above the KB root is DISCARDED, never clamped;
//   * a directory target (trailing `/`) and a target that normalises to no
//     segment at all address nothing;
//   * a CommonMark target is percent-decoded exactly once, safely, before it
//     is resolved; a `[[wikilink]]` target is never decoded, and gains `.md`
//     when its final segment carries no extension.
//
// It was written twice by hand — the server's Linked Memories extractor
// (`apps/mcp/src/core/markdown/links.ts`, LAT-1110) and the browser's link
// verdict (`apps/web/src/lib/kb-links.ts`, LAT-1113) — each with a header
// promising to stay "byte-for-byte" the other, and a third `completeExt` sat
// in this package's own `./remark-wikilinks.ts`. LAT-1867 then needed a
// FOURTH reader: `@atmina/evidence` must resolve a body span link the way the
// viewer opens it, or an attestation is hashed against the wrong file. Four
// hand-kept copies of one rule is the divergence ADR 0049 §1 names and
// refuses — silent, and found only when a citation the viewer opens is one
// the reader cannot bind. So the rule is defined here, once: the browser and
// the remark plugin now import it and their copies are gone;
// `apps/web/test/lib/kb-links.test.ts` runs unchanged against this
// implementation, which is the byte-identity proof. The server extractor's
// copy REMAINS for now — `apps/mcp` does not declare `@atmina/kb-view`, and
// adding the manifest line re-resolved kb-view's `vitest: "latest"` in the
// lockfile, so folding it is a LAT-1867 follow-up. Until then this file is
// the reference and `links.ts` is held byte-identical to it.
//
// What deliberately did NOT move: the external-target test (`https:`,
// `mailto:`, `//host`, `#fragment`), self-link handling, and the catalog
// match. Those are each consumer's own verdict — the extractor discards a
// self-link, the viewer renders it as an inert self-reference, the evidence
// reader binds it — and they were never part of the path rule.
//
// This is a React-free entry point (`@atmina/kb-view/kb-link-target`), for
// the same reason `./source-span` is one: `@atmina/evidence` loads inside
// workerd and must read this without pulling React into its program. It has
// no imports, and must keep none.

/**
 * Why a target addresses no KB path. Each is a verdict the browser renders as
 * a specific inert reference and the server extractor discards.
 */
export type InvalidKbLinkTargetReason = "directory" | "root-escape" | "empty";

/**
 * A resolved target: the canonical KB path it addresses, or the reason it
 * addresses none. Every variant is explicit so a consumer cannot mistake a
 * discarded target for a path.
 */
export type KbLinkTargetResolution =
  | { kind: "path"; path: string }
  | { kind: "invalid"; reason: InvalidKbLinkTargetReason };

/**
 * Percent-decode exactly once, safely: a malformed `%` sequence (not followed
 * by two hex digits) stays a literal `%` — never a throw. A catalog path
 * containing a literal `%` therefore matches only a link that encodes it as
 * `%25` (PRD §6).
 */
export function safePercentDecode(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "%" && /^[0-9a-fA-F]{2}$/.test(s.slice(i + 1, i + 3))) {
      out += String.fromCharCode(parseInt(s.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Resolve a raw KB-internal target against the source file's directory
 * (relative) or the KB root (`/…`). Invalid targets carry the exact reason
 * that makes the server discard them; valid targets carry their canonical
 * path. Escaping targets are DISCARDED, never clamped (PRD §6).
 *
 * The caller decides what to decode first: a CommonMark target is passed
 * through {@link safePercentDecode}; a wikilink target is passed through
 * {@link completeWikilinkExtension} and never decoded.
 */
export function resolveKbLinkPath(
  target: string,
  srcPath: string,
): KbLinkTargetResolution {
  if (target.endsWith("/")) return { kind: "invalid", reason: "directory" };
  const rooted = target.startsWith("/");
  const base = rooted ? [] : srcPath.split("/").slice(0, -1);
  const stack = [...base];
  for (const seg of (rooted ? target.slice(1) : target).split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      if (stack.length === 0) {
        return { kind: "invalid", reason: "root-escape" };
      }
      stack.pop();
      continue;
    }
    stack.push(seg);
  }
  if (stack.length === 0) return { kind: "invalid", reason: "empty" };
  return { kind: "path", path: stack.join("/") };
}

/**
 * Append `.md` when a wikilink target's final segment carries no extension
 * (LAT-1157, PRD §3.1). Directory targets (trailing slash) and empty targets
 * are left untouched — {@link resolveKbLinkPath} discards them.
 */
export function completeWikilinkExtension(target: string): string {
  if (target === "" || target.endsWith("/")) return target;
  const base = target.slice(target.lastIndexOf("/") + 1);
  return base.includes(".") ? target : `${target}.md`;
}
